import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Row, Col } from 'react-bootstrap';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { safeDivide } from 'helpers/utilities';
import { reduceLayerTwoData } from 'helpers/data';
import RenderingTile from 'components/molecules/RenderingTile';
import DonutChart from 'components/molecules/charts/DonutChart';
import ExpandedDonut from 'components/molecules/ExpandedDonut';
import SocialAdSetOverviewPivotTable from 'components/molecules/tables/SocialAdSetOverviewPivotTable';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import SocialConversionsChart from 'components/molecules/charts/SocialConversionsChart';
import { LAYERTHREE, LAYERTWO } from 'constants/layers';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import SocialActionOverviewTable from 'components/molecules/tables/SocialActionOverviewTable';

const DONUTCHARTMAX = 10;

class LeadGenerationExpandModule extends Component {
    render() {
        const {
            backfillBuckets,
            xAxisLabel,
            layer = LAYERTHREE,
            layerTwo,
            showConversionRevenue,
            entityMeta,
            entityObjective,
            entityObjectiveLabelCode
        } = this.props;
        const {
            conversionTermSingular,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);
        const { ads, adsets, campaigns, timeData, actions } = this.props.module;
        const stub = `l${layer}-social`;
        const hasAdSetData = adsets.hasData;
        const hasAdsData = ads.hasData;
        const hasTimeData = get(timeData, 'hasData', null);
        const totalLeads = get(this.props, 'module.ads.statistics.leadgen_grouped', 0);
        const l2Data = get(layerTwo, 'social', {});
        const hasLayerTwoSummaryData = get(l2Data, 'summary.hasData', null);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');
        const userId = get(this.props, 'currentUser.id', null);
        const entityId = get(this.props, 'entityId', null);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(l2Data.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }

        const rankedLeadsByAdSet = hasAdSetData ? adsets.rankings.leadgen_grouped.reduce((prev, data) => {
            const adsetName = adsets.adSetIdMap[data.name].name;
            if (data.value > 0) {
                prev.data.push({
                    name: adsetName,
                    y: safeDivide(data.value, totalLeads)
                });
            }

            return prev;
        }, { data: [] }) : {};
        const rankedLeadsByAd = hasAdsData ? ads.rankings.leadgen_grouped.reduce((prev, data) => {
            const adName = ads.adIdMap[data.name].name;
            if (data.value > 0) {
                prev.data.push({
                    name: adName,
                    y: safeDivide(data.value, totalLeads)
                });
            }
            return prev;
        }, { data: [] }) : {};

        const showExpandedDonut = (hasAdSetData && hasAdsData) ?
            rankedLeadsByAd.data.length > DONUTCHARTMAX || rankedLeadsByAdSet.data.length > DONUTCHARTMAX : false;
        const Donut = showExpandedDonut ? ExpandedDonut : DonutChart;
        const objectives = get(entityMeta, 'social.objectives', []);
        const showLayerTwoSummaryTable = !!(hasLayerTwoSummaryData && (layer === LAYERTWO) && (objectives.length > 1));
        const showCampaignSummaryTable = !!(hasAdSetData && layer === LAYERTHREE);

        return (
            <React.Fragment>
                {/* Leads vs. Cost per Lead */}
                {hasTimeData ? (
                    <TwoAxisBarWithScatterChart
                        title='Leads VS. Cost Per Lead '
                        exportName={`${stub}-lead-generation-lead-vs-cost-per-lead`}
                        animateDuration={2000}
                        data={timeData.data}
                        scatter="cost_per_leadgen_grouped"
                        scatterTitle="Cost / Lead"
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        xAxisType="datetime"
                        yBar="leadgen_grouped"
                        yBarTitle="Leads"
                        yBarOptions={{
                            allowDecimals: false
                        }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasTimeData} />
                )}

                {/* Impressions vs. Conversion Rate */}
                {hasTimeData ? (
                    <TwoAxisBarWithLineChart
                        title='Impressions vs. Lead Conversion Rate'
                        exportName={`${stub}-lead-generation-impressions-vs-lead-conversion-rate`}
                        data={timeData.data}
                        yBar="impressions"
                        yLine="leadgen_grouped_rate"
                        yLineTitle="Lead Rate"
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        yLineOptions={{ formatByType: 'percentage' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='465px' show={hasTimeData} />
                )}


                <Row>
                    <Col md={showExpandedDonut ? 12 : 6}>
                        {hasAdSetData ? (
                            <Donut
                                title='Leads By Ad Set'
                                exportName={`${stub}-lead-generation-leads-by-adset`}
                                data={rankedLeadsByAdSet.data}
                                y="adsets"
                                total={totalLeads}
                                totalTitle="Leads"
                                totalFormat='0,0' />
                        ) : (
                            <RenderingTile height='465px' show={hasAdSetData} />
                        )}
                    </Col>

                    <Col md={showExpandedDonut ? 12 : 6}>
                        {hasAdsData ? (
                            <Donut
                                title='Leads By Ad'
                                exportName={`${stub}-lead-generation-leads-by-ad`}
                                data={rankedLeadsByAd.data}
                                y="ads"
                                total={totalLeads}
                                totalTitle="Leads"
                                totalFormat='0,0'
                                inverted
                            />
                        ) : (
                            <RenderingTile height='465px' show={hasAdsData} />
                        )}
                    </Col>
                </Row>

                <SocialConversionsChart actions={actions} exportNamePrefix={`${stub}-lead-generation`} />

                {/* Campaign Overview */}
                {showCampaignSummaryTable ? (
                    <SocialAdSetOverviewPivotTable
                        title="Campaign Overview"
                        exportName="l3-social-lead-generation-summary-adsets-overview"
                        adSets={adsets}
                        showConversionRevenue={showConversionRevenue}
                        accessors={['impressions', 'clicks', 'cpc', 'link_clicks', 'cost_per_link_click', 'leadgen_grouped', 'cost_per_leadgen_grouped', 'conversions', 'engagements', 'budget']}
                    />
                ) : (
                    <RenderingTile height='400px' show={showCampaignSummaryTable} />
                )}

                {showLayerTwoSummaryTable ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={'social'}
                        showConversionRevenue={showConversionRevenue}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        exportName='l2-social-lead-generation-table-performance'
                        accessors={['impressions', 'clicks', 'cpc', 'link_clicks', 'cost_per_link_click', 'leadgen_grouped', 'cost_per_leadgen_grouped', 'conversions', 'engagements', 'spend']}
                        cacheKey={`l2-social-lead-generation-table-performance-${userId}-${entityId}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showLayerTwoSummaryTable} />
                )};

                <SocialActionOverviewTable
                    exportName={`${stub}-lead-generation-action-overview`}
                    actions={actions}
                    campaigns={campaigns}
                    conversionTermSingular={conversionTermSingular}
                    cacheKey={`${stub}-lead-generation-action-overview-${userId}-${entityId}`}
                />

            </React.Fragment>
        );
    }
}

LeadGenerationExpandModule.propTypes = {
    module: PropTypes.shape({
        actions: PropTypes.object,
        campaigns: PropTypes.object,
        adsets: PropTypes.object,
        ads: PropTypes.object,
        timeData: PropTypes.object
    }).isRequired
};

export default LeadGenerationExpandModule;



// WEBPACK FOOTER //
// ./src/components/organisms/social/LeadGenerationExpandModule.js